/**
 * <s-school-filter> — Filters a list of school links as the shopper types.
 *
 * Required child markup:
 *   input[type="search"]      — The filter input.
 *   [data-school]             — Each filterable item; its data-school holds the searchable name.
 *   [data-school-empty]       — Message shown when nothing matches (hidden by default).
 *
 * Element dataset:
 *   data-results-one / data-results-other — Translated announcement templates using [count].
 */
import { announceGlobalLiveRegion } from '@theme/live-region';

class SSchoolFilter extends HTMLElement {
  #ac;
  #timer;

  connectedCallback() {
    this.#ac = new AbortController();
    const input = this.querySelector('input[type="search"]');
    if (!input) return;
    input.addEventListener('input', () => this.#filter(input.value), { signal: this.#ac.signal });
    // A submitted filter should not navigate; Enter opens the first visible school instead.
    input.form?.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        const first = this.querySelector('[data-school]:not([hidden]) a');
        if (first) first.click();
      },
      { signal: this.#ac.signal }
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  #filter(value) {
    const term = value.trim().toLowerCase();
    let visible = 0;
    for (const item of this.querySelectorAll('[data-school]')) {
      const match = term === '' || item.dataset.school.toLowerCase().includes(term);
      item.hidden = !match;
      if (match) visible += 1;
    }
    const empty = this.querySelector('[data-school-empty]');
    if (empty) empty.hidden = visible > 0;

    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      const template = visible === 1 ? this.dataset.resultsOne : this.dataset.resultsOther;
      if (template) announceGlobalLiveRegion(template.replace('[count]', String(visible)));
    }, 400);
  }
}

if (!customElements.get('s-school-filter')) customElements.define('s-school-filter', SSchoolFilter);
